import React, { useState, useCallback } from "react";
import { useLang } from "../i18n/LangContext";
import "./Screen4.css";

interface ChargeRow {
  id: number;
  desc: string;
  amount: string;
  taxPct: string;
}

const euro = (n: number) => new Intl.NumberFormat("fi-FI", { style: "currency", currency: "EUR" }).format(n);
const parseNum = (v: string) => Number(v.trim().replace(",", ".")) || 0;

let rowId = 0;
const makeRow = (desc = "", amount = "", taxPct = "24"): ChargeRow => ({ id: ++rowId, desc, amount, taxPct });

const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const addDays = (days: number) => { const d = new Date(); d.setDate(d.getDate() + days); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

const Screen4: React.FC = () => {
  const { t } = useLang();
  const s = t.screens.s4;
  const [rows, setRows] = useState<ChargeRow[]>([makeRow()]);
  const [toast, setToast] = useState("");

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 1200);
  }, []);

  const updateRow = (id: number, field: keyof ChargeRow, value: string) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const removeRow = (id: number) => setRows((prev) => prev.filter((r) => r.id !== id));

  const subtotal = rows.reduce((sum, r) => sum + parseNum(r.amount), 0);
  const taxTotal = rows.reduce((sum, r) => sum + parseNum(r.amount) * (parseNum(r.taxPct) / 100), 0);

  const fillDemo = () => {
    setRows(s.demoRows.map((r) => makeRow(r.desc, r.amount, r.tax)));
    showToast(s.demoFilled);
  };

  return (
    <div className="s4-page">
      <nav className="topnav">
        <div className="logo">Worklly</div>
        <div className="tabs">
          <div className="tab">{s.dashboard}</div>
          <div className="tab">{s.employees}</div>
          <div className="tab active">{s.invoicing}</div>
          <div className="tab">{s.reports}</div>
        </div>
        <div className="right">
          <div className="icon-btn">&#128276;</div>
          <div className="icon-btn">&#9776;</div>
          <div className="icon-btn">&#9881;</div>
          <div className="avatar">AM</div>
        </div>
      </nav>

      <div className="s4-layout">
        <aside className="s4-sidebar">
          <h3>{s.invoicing}</h3>
          <div className="nav-item active"><span className="icon">&#128196;</span> {s.createInvoice}</div>
          <div className="nav-item"><span className="icon">&#128203;</span> {s.allInvoices} <span className="badge-count">12</span></div>
          <div className="nav-item"><span className="icon">&#128178;</span> {s.payments} <span className="badge-count">8</span></div>
          <h3>{s.customers}</h3>
          <div className="nav-item"><span className="icon">&#128101;</span> {s.customers} <span className="badge-count">6</span></div>
          <div className="nav-item"><span className="icon">&#128202;</span> {s.projects} <span className="badge-count">4</span></div>
          <div className="nav-item"><span className="icon">&#9881;</span> {s.settings}</div>
          <div className="sidebar-footer"><span className="status-dot" /> {s.invoiceBuilderActive}</div>
        </aside>

        <div className="s4-main">
          <div className="page-header">
            <div>
              <h1>{s.pageTitle}</h1>
              <div className="subtitle">{s.pageSubtitle}</div>
            </div>
            <div className="header-actions">
              <button className="btn" onClick={() => setRows((p) => [...p, makeRow()])}>{s.addRow}</button>
              <button className="btn" onClick={fillDemo}>{s.fillDemo}</button>
              <button className="btn btn-primary" onClick={() => showToast(s.pdfGenerated)}>{s.generatePdf}</button>
            </div>
          </div>

          <div className="two-col">
            <div className="card">
              <div className="card-title">{s.invoiceDetails}</div>
              <div className="card-desc">{s.invoiceDetailsDesc}</div>
              <div className="form-row">
                <div className="form-group"><label>{s.customerName}</label><input defaultValue="Arctic Consulting Oy" /></div>
                <div className="form-group"><label>{s.customerEmail}</label><input defaultValue="billing@arctic.example" /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>{s.invoiceNumber}</label><input defaultValue={`KAV-${new Date().getFullYear()}-${Math.floor(Math.random() * 9000) + 1000}`} /></div>
                <div className="form-group"><label>{s.invoiceDate}</label><input type="date" defaultValue={today()} /></div>
                <div className="form-group"><label>{s.dueDate}</label><input type="date" defaultValue={addDays(14)} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>{s.notes}</label><textarea placeholder={s.notesPlaceholder} /></div>
              </div>
              <div className="card-title" style={{ marginTop: 16 }}>{s.charges}</div>
              <table className="charges-table">
                <thead><tr><th style={{ width: "48%" }}>{s.thDesc}</th><th className="num" style={{ width: "16%" }}>{s.thAmount}</th><th className="num" style={{ width: "12%" }}>{s.thTaxPct}</th><th className="num" style={{ width: "16%" }}>{s.thTax}</th><th style={{ width: 40 }}></th></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td><input value={r.desc} onChange={(e) => updateRow(r.id, "desc", e.target.value)} placeholder={s.descPlaceholder} /></td>
                      <td className="num"><input value={r.amount} onChange={(e) => updateRow(r.id, "amount", e.target.value)} placeholder="0.00" style={{ textAlign: "right" }} /></td>
                      <td className="num"><input value={r.taxPct} onChange={(e) => updateRow(r.id, "taxPct", e.target.value)} placeholder="0" style={{ textAlign: "right" }} /></td>
                      <td className="num" style={{ fontWeight: 600, color: "#6b7280" }}>{euro(parseNum(r.amount) * (parseNum(r.taxPct) / 100))}</td>
                      <td><button className="remove-btn" onClick={() => removeRow(r.id)}>&times;</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="info-badge"><span style={{ width: 6, height: 6, borderRadius: "50%", background: "#2b6cee" }} /> {s.ibanNote}</div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div className="card">
                <div className="card-title">{s.totals}</div>
                <div className="card-desc">{s.totalsDesc}</div>
                <div className="totals-list">
                  <div className="total-row"><span className="label">{s.subtotal}</span><span className="value">{euro(subtotal)}</span></div>
                  <div className="total-row"><span className="label">{s.taxTotal}</span><span className="value">{euro(taxTotal)}</span></div>
                  <div className="total-row grand"><span className="label">{s.totalToPay}</span><span className="value">{euro(subtotal + taxTotal)}</span></div>
                </div>
              </div>
              <div className="card">
                <div className="card-title">{s.paymentDetails}</div>
                <div className="card-desc">{s.paymentDetailsDesc}</div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 14px", fontSize: 13 }}><span style={{ color: "#6b7280", fontWeight: 500 }}>{s.receiver}</span><span style={{ fontWeight: 700 }}>Worklly Oy</span></div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 14px", fontSize: 13 }}><span style={{ color: "#6b7280", fontWeight: 500 }}>{s.iban}</span><span style={{ fontWeight: 700 }}>FI02 1761 3500 0049</span></div>
              </div>
              <div className="card" style={{ background: "#f9fafb" }}>
                <div className="card-title">{s.recentInvoices}</div>
                <div className="card-desc">{s.recentInvoicesDesc}</div>
                {[
                  { no: "KAV-2025-0412", client: "Nordic Solutions Oy", amount: "€2,480.00", status: s.paid, statusColor: "#16a34a" },
                  { no: "KAV-2025-0389", client: "Helsinki Design Co", amount: "€1,860.00", status: s.pending, statusColor: "#f59e0b" },
                  { no: "KAV-2025-0344", client: "Arctic Consulting Oy", amount: "€3,120.00", status: s.paid, statusColor: "#16a34a" },
                ].map((inv, i) => (
                  <div className="total-row" style={{ background: "#fff", marginTop: 8 }} key={i}>
                    <div><div style={{ fontWeight: 700, fontSize: 13 }}>{inv.no}</div><div style={{ fontSize: 11, color: "#6b7280" }}>{inv.client}</div></div>
                    <div style={{ textAlign: "right" }}><div style={{ fontWeight: 700, fontSize: 13 }}>{inv.amount}</div><div style={{ fontSize: 11, color: inv.statusColor, fontWeight: 600 }}>{inv.status}</div></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`toast${toast ? " show" : ""}`}>{toast}</div>
    </div>
  );
};

export default Screen4;
